
import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface Plan {
  id: string;
  product: {
    id: string;
    name: string;
    description: string;
    metadata: {
      features?: string;
    };
  };
  unit_amount: number;
  currency: string;
  recurring: {
    interval: string;
    interval_count: number;
  };
}

interface Subscription {
  id: string;
  status: string;
  current_period_end: number;
  cancel_at_period_end: boolean;
  plan: {
    nickname: string;
    amount: number;
    currency: string;
    interval: string;
  };
}

export const useSubscription = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState<string | null>(null);

  // Fetch customer data from the database
  useEffect(() => {
    const fetchCustomerId = async () => {
      if (!user) return;
      
      try {
        // Check if user has a customerId in profiles table
        const { data, error } = await supabase
          .from('profiles')
          .select('stripe_customer_id')
          .eq('id', user.id)
          .single();
        
        if (error) throw error;
        
        if (data && data.stripe_customer_id) {
          setCustomerId(data.stripe_customer_id);
        } else {
          // Create a new customer
          const response = await supabase.functions.invoke('stripe-functions', {
            body: { action: 'createCustomer', email: user.email },
          });
          
          if (response.error) throw new Error(response.error.message);
          
          const newCustomerId = response.data.id;
          
          // Save the customer ID to the profiles table
          const { error: updateError } = await supabase
            .from('profiles')
            .update({ stripe_customer_id: newCustomerId })
            .eq('id', user.id);
          
          if (updateError) throw updateError;
          
          setCustomerId(newCustomerId);
        }
      } catch (error) {
        console.error('Error fetching customer ID:', error);
        toast({
          title: "Error fetching billing data",
          description: "Please try again later.",
          variant: "destructive",
        });
      }
    };
    
    fetchCustomerId();
  }, [user, toast]);

  // Fetch subscription plans and user's subscription status
  useEffect(() => {
    const fetchBillingData = async () => {
      if (!customerId) return;
      
      setLoading(true);
      
      try {
        // Fetch available plans
        const plansResponse = await supabase.functions.invoke('stripe-functions', {
          body: { action: 'getPlans' },
        });
        
        if (plansResponse.error) throw new Error(plansResponse.error.message);
        setPlans(plansResponse.data.data);
        
        // Fetch user's subscription
        const subscriptionsResponse = await supabase.functions.invoke('stripe-functions', {
          body: { action: 'getSubscriptions', customerId },
        });
        
        if (subscriptionsResponse.error) throw new Error(subscriptionsResponse.error.message);
        
        const activeSubscription = subscriptionsResponse.data.data.find(
          (sub: any) => ['active', 'trialing'].includes(sub.status)
        );
        
        setSubscription(activeSubscription || null);
      } catch (error) {
        console.error('Error fetching billing data:', error);
        toast({
          title: "Error fetching billing data",
          description: "Please try again later.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchBillingData();
  }, [customerId, toast]);

  const handleSubscribe = async (priceId: string) => {
    if (!customerId) return;
    
    setProcessingPlan(priceId);
    
    try {
      const response = await supabase.functions.invoke('stripe-functions', {
        body: { 
          action: 'createCheckoutSession', 
          customerId, 
          priceId,
        },
      });
      
      if (response.error) throw new Error(response.error.message);
      
      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast({
        title: "Error creating checkout",
        description: "Please try again later.",
        variant: "destructive",
      });
      setProcessingPlan(null);
    }
  };

  const handleManageSubscription = async () => {
    if (!customerId) return;
    
    setLoading(true);
    
    try {
      const response = await supabase.functions.invoke('stripe-functions', {
        body: { 
          action: 'createPortalSession', 
          customerId,
        },
      });
      
      if (response.error) throw new Error(response.error.message);
      
      // Redirect to Stripe customer portal
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Error creating portal session:', error);
      toast({
        title: "Error opening billing portal",
        description: "Please try again later.",
        variant: "destructive",
      });
      setLoading(false);
    }
  };

  return {
    plans,
    subscription, 
    customerId,
    loading,
    processingPlan,
    handleSubscribe,
    handleManageSubscription
  };
};
